import { GAME_ASSET_SLUG } from "@/config/game";
import { Button } from "@/design-system/Button/Button";
import { Dropdown, DropdownItem } from "@/design-system/Dropdown/Dropdown";
import Input from "@/design-system/Input/Input";
import { SegmentedControl } from "@/design-system/SegmentedControl/SegmentedControl";
import { Switch } from "@/design-system/Switch/Switch";
import { NewTag } from "@/design-system/Tags/NewTag/NewTag";
import { TextButton } from "@/design-system/TextButton/TextButton";
import { cn } from "@/lib/utils";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import { usesClubPool } from "@repo/shared/redux";
import { ChevronRight } from "lucide-react";
import { ReactNode, useMemo, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery, useResizeObserver } from "usehooks-ts";

import { APIStatus } from "../../../../../api/APIStatus.type";
import { SavedSquad } from "../../../../../api/privateRequests/squad-builder/get-squads";
import {
  CoinIcon,
  binIcon,
  both,
  club,
  clubsIcon,
  flagIcon,
  leagueIcon,
  market,
} from "../../../../../designSystemIcons";
import {
  TOP_FIVE_LEAGUE_IDS,
  TOP_NINE_COUNTRY_IDS,
  loadedContentSelector,
} from "../../../../../redux/loadedContent/loadedContentSlice";
import { addAlert } from "../../../../../redux/message/messageSlice";
import {
  defaultSquadBuilderSelector,
  lockedFormationForLiveEvent,
  selectLiveEvent,
} from "../../../../../redux/squadBuilder/defaultSquadBuilder/defaultSquadBuilderSlice";
import {
  RatingFilter as RatingFilterType,
  Scope,
} from "../../../../../redux/squadBuilder/squadBuilderUtils";
import { AppDispatch } from "../../../../../redux/store";
import Tooltip from "../../../../atoms/Tooltip/Tooltip";
import { OnBoardingNumber } from "../../../OnBoarding/OnBoardingNumber";
import BestFormationIncludeSectionOnPitch from "../../components/BestFormation/BestFormationIncludeSectionOnPitch";
import { ComponentContainer } from "../../components/ComponentContainer/ComponentContainer";
import { SuggestSquadsButton } from "../../components/SuggestSquadsButton/SuggestSquadsButton";
import {
  FilterDropdown,
  toClubItems,
  toLeagueItems,
} from "../FilterDropdown/FilterDropdown";
import { PanelHeader } from "../PanelHeader/PanelHeader";

// Short labels for long live event names; others show as named.
export const LIVE_EVENT_SHORT_NAMES: Record<string, string> = {
  "Ones We Watched Professional Challenge": "OWW Professional",
  "Ones We Watched World Class Challenge": "OWW World Class",
  "Ones We Watched Legendary Challenge": "OWW Legendary",
  "Season 1: Ones to Watch Exhibition": "Ones to Watch Exhibition",
};

// The live event dropdown's "none" entry
const NO_LIVE_EVENT = "none";

// The saved squads dropdown's "add new" entry
const ADD_NEW_SQUAD = "addNew";

const SCOPES: Record<Scope, (active: boolean) => ReactNode> = {
  market,
  both,
  club,
};

const assetsBaseUrl = import.meta.env.VITE_APP_ASSETS_BASE_URL;

// The top nations and leagues first, then by name
const topFirst =
  (top: number[]) =>
  (a: { id: number; name: string }, b: { id: number; name: string }) =>
    Number(top.includes(b.id)) - Number(top.includes(a.id)) ||
    a.name.localeCompare(b.name);

// Its own component, so hovering a scope re-renders only this
const ScopePicker = ({
  scope,
  onChange,
}: {
  scope: Scope;
  onChange: (scope: Scope) => void;
}) => {
  const { t } = useTranslation("squadBuilder");
  const [hoveredScope, setHoveredScope] = useState<Scope>(null);
  // Icons only: the label above names the scope, hovered or picked
  const options = (Object.keys(SCOPES) as Scope[]).map((value) => ({
    value,
    label: (
      <span
        className="flex w-full justify-center py-0.5"
        onMouseEnter={() => setHoveredScope(value)}
        onMouseLeave={() => setHoveredScope(null)}
      >
        <span className="size-6">{SCOPES[value](value === scope)}</span>
        <span className="sr-only">{t(`scope.${value}`)}</span>
      </span>
    ),
  }));
  return (
    <div className="flex flex-col w-full gap-y-1">
      <span className="pl-3 text-xs text-foreground-muted">
        {t("scope.label", { scope: t(`scope.${hoveredScope ?? scope}`) })}
      </span>
      <SegmentedControl
        label={t("customize.scopeLabel")}
        variant="primary"
        track="page"
        value={scope}
        onChange={onChange}
        options={options}
        className="w-full [&>button]:flex-1 [&>button]:p-0"
      />
    </div>
  );
};

type Props = {
  statusBuildSquad: APIStatus;
  budget: number;
  ratingFilter: RatingFilterType;
  scope: Scope;
  selectedCountries: number[];
  selectedLeagues: number[];
  selectedClubs: number[];
  savedSquads: SavedSquad[];
  handleClickedBuildSquads: () => void;
  handleBudgetUpdated: (budget: number) => void;
  handleMinRatingChange: (minRating: number) => void;
  handleMaxRatingChange: (maxRating: number) => void;
  handleScopeChange: (scope: Scope) => void;
  handleSelectedCountry: (id: number) => void;
  handleSelectedLeague: (id: number) => void;
  handleSelectedClub: (id: number) => void;
  handleClickMoreFilters: () => void;
  numberOfPlayersWithFilters: number;
  handleUpdateFormation: (formationId: string, isTemp: boolean) => void;
  onBoardingStep?: number;
  chemOptimizing?: boolean;
  desktopMode?: boolean;
  loadedSaveSquadIndex?: number;
  handleLoadSquad: (index: number) => void;
  handleAddNewSquad: () => void;
  handleDeleteSquad: (squadId: string) => Promise<void>;
  selectedFormation: string;
  handleBestFormationChange: (isBestFormation: boolean) => void;
  isBestFormationOn: boolean;
  activeFilterCount?: number;
  handleClearAllFilters?: () => void;
  /** Spotlights the customize card once, after a squad was picked via the dialog. */
  highlightFilters?: boolean;
};

export const InputMode = ({
  statusBuildSquad,
  budget,
  scope,
  selectedCountries,
  selectedLeagues,
  selectedClubs,
  savedSquads,
  handleUpdateFormation,
  handleClickedBuildSquads,
  handleBudgetUpdated,
  handleScopeChange,
  handleSelectedCountry,
  handleSelectedLeague,
  handleSelectedClub,
  handleClickMoreFilters,
  onBoardingStep,
  chemOptimizing,
  desktopMode,
  loadedSaveSquadIndex,
  handleLoadSquad,
  handleAddNewSquad,
  handleDeleteSquad,
  selectedFormation,
  handleBestFormationChange,
  isBestFormationOn,
  activeFilterCount = 0,
  handleClearAllFilters,
  highlightFilters,
}: Props) => {
  const { t } = useTranslation(["squadBuilder", "common"]);
  const {
    countries: loadedCountries,
    leagues: loadedLeagues,
    clubs: loadedClubs,
    formations,
    liveEvents,
  } = useSelector(loadedContentSelector);

  const formationDropdownOptions = useMemo(
    () =>
      isBestFormationOn
        ? [{ label: t("common:any"), value: "" }]
        : Object.keys(formations).map((formationId) => ({
            label: formations[formationId].label,
            value: formationId,
          })),
    [formations, isBestFormationOn, t],
  );

  const dispatch = useDispatch<AppDispatch>();
  const { selectedLiveEventId } = useSelector(defaultSquadBuilderSelector);
  const isFormationLocked = !!lockedFormationForLiveEvent(selectedLiveEventId);
  const isMobileScreen = useMediaQuery(BREAKPOINTS.MOBILE.query);
  // Full-width dropdowns span the filters column, as the filter rows do
  const filtersRef = useRef<HTMLDivElement>(null);
  const { width: filtersWidth } = useResizeObserver({
    ref: filtersRef,
    box: "border-box",
  });

  // Rows hold the squad index; the bin is a span since the row is a button
  const savedSquadItems: DropdownItem[] = [
    ...savedSquads.map((squad, index) => ({
      value: String(index),
      label: squad.name,
      render: (
        <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
          <span className="truncate">{squad.name}</span>
          <span
            role="button"
            aria-label={t("common:delete")}
            className={cn(
              "shrink-0 text-foreground-muted hover:text-foreground",
              !isMobile && "invisible [button:hover_&]:visible",
            )}
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteSquad(squad.id)
                .then(() => {
                  dispatch(
                    addAlert({
                      description: t("alerts.deletedSquad"),
                      type: "success",
                    }),
                  );
                })
                .catch(() => {
                  dispatch(
                    addAlert({
                      description: t("alerts.deleteFailed"),
                      type: "error",
                    }),
                  );
                });
            }}
          >
            {binIcon}
          </span>
        </span>
      ),
    })),
    { value: ADD_NEW_SQUAD, label: t("input.addNew") },
  ];

  const countries = useMemo<DropdownItem[]>(
    () =>
      [...loadedCountries].sort(topFirst(TOP_NINE_COUNTRY_IDS)).map((c) => ({
        value: String(c.id),
        label: c.name,
        icon: (
          <img
            className="w-5"
            src={`${assetsBaseUrl}/${GAME_ASSET_SLUG}/countries/${c.id}.png?scale=50`}
            alt={c.name}
          />
        ),
      })),
    [loadedCountries],
  );

  const leagues = useMemo(
    () => toLeagueItems([...loadedLeagues].sort(topFirst(TOP_FIVE_LEAGUE_IDS))),
    [loadedLeagues],
  );

  const clubs = useMemo(() => toClubItems(loadedClubs), [loadedClubs]);

  const liveEventItems = useMemo<DropdownItem[]>(
    () => [
      { value: NO_LIVE_EVENT, label: t("input.noLiveEvent") },
      ...liveEvents.map((e) => ({
        value: String(e.id),
        label: LIVE_EVENT_SHORT_NAMES[e.name] ?? e.name,
      })),
    ],
    [liveEvents, t],
  );

  const budgetDisplay =
    budget === Number.MAX_VALUE ? "" : budget.toLocaleString("en-US");

  return (
    <>
      {!desktopMode && <PanelHeader title={t("panel.squadFilters")} />}
      <div className="relative top-0 flex flex-col items-center w-full py-4 text-foreground touch-device:absolute touch-device:pb-[calc(var(--ad-gutter)+1rem)] sb-v2-lg:px-8">
        <div className="w-full">
          <ComponentContainer marginBottom="mb-4 mt-4">
            {savedSquads.length > 0 && (
              <div className="flex flex-col w-full gap-y-1">
                <span className="pl-3 text-xs text-foreground-muted">
                  {t("input.savedSquads")}
                </span>
                <Dropdown
                  // Remount after a delete: the open panel keeps a snapshot of its rows
                  key={savedSquads.length}
                  items={savedSquadItems}
                  selected={
                    loadedSaveSquadIndex === undefined
                      ? undefined
                      : String(loadedSaveSquadIndex)
                  }
                  placeholder={t("input.addSquad")}
                  onChange={(item) =>
                    item.value === ADD_NEW_SQUAD
                      ? handleAddNewSquad()
                      : handleLoadSquad(Number(item.value))
                  }
                  variant="secondary"
                  hideSearch
                  width={filtersWidth}
                />
              </div>
            )}
            <div className="flex flex-row w-full mt-4 gap-x-1 items-center">
              <span className="ml-3 text-xs text-foreground-body">
                {t("input.formation")}
              </span>
              <div className="flex flex-row w-full justify-end">
                <Tooltip
                  content={t("input.formationLockedByLiveEvent")}
                  disabled={!isFormationLocked}
                  triggerClassName="[&_*]:pointer-events-none"
                >
                  <Dropdown
                    items={formationDropdownOptions}
                    selected={isBestFormationOn ? "" : selectedFormation}
                    placeholder={t("input.formation")}
                    onChange={(item) =>
                      handleUpdateFormation(item.value, false)
                    }
                    variant="secondary"
                    hideSearch
                    width={160}
                    align="end"
                    disabled={isBestFormationOn || isFormationLocked}
                  />
                </Tooltip>
              </div>
            </div>
            <div className="flex flex-row w-full mt-4 justify-between items-center">
              <label className="ml-3 text-xs text-foreground-body flex flex-row items-center gap-x-1">
                {t("input.bestFormation")}
              </label>
              <Tooltip
                content={t("input.formationLockedByLiveEvent")}
                disabled={!isFormationLocked}
                triggerClassName="[&_*]:pointer-events-none"
              >
                <Switch
                  onChange={handleBestFormationChange}
                  isActive={isBestFormationOn}
                  disabled={isFormationLocked}
                />
              </Tooltip>
            </div>
            {isBestFormationOn && isMobileScreen && (
              <BestFormationIncludeSectionOnPitch
                isMobileAndInInputMode={isMobileScreen}
                handleClickedBuildSquads={handleClickedBuildSquads}
                onClose={() => handleBestFormationChange(false)}
              />
            )}
          </ComponentContainer>
        </div>
        {highlightFilters && (
          <div className="absolute inset-0 z-20 bg-black/65 motion-safe:animate-hint-fade" />
        )}
        <div className={cn("relative w-full", highlightFilters && "z-21")}>
          <ComponentContainer
            title={
              <span className="whitespace-nowrap text-xs">
                {t("input.customizeAi")}
              </span>
            }
          >
            <div className="flex flex-col w-full gap-y-4">
              <div className="flex flex-col w-full gap-y-1">
                <span className="pl-3 text-xs text-foreground-muted">
                  {t("input.budget")}
                </span>
                <Input
                  size="small"
                  className="text-sm"
                  inputMode="numeric"
                  icon={<CoinIcon size={16} />}
                  placeholder={t("input.budgetPlaceholder")}
                  value={budgetDisplay}
                  onChange={(value) => {
                    const digits = value.replace(/[^\d]/g, "");
                    handleBudgetUpdated(
                      digits === "" ? Number.MAX_VALUE : Number(digits),
                    );
                  }}
                />
                {Number(budget) && budget !== Number.MAX_VALUE && (
                  <span className="pl-3 text-xs text-foreground-muted">
                    {t("input.budgetNote")}
                  </span>
                )}
              </div>
              <ScopePicker scope={scope} onChange={handleScopeChange} />
              <div className="flex flex-col w-full gap-y-1">
                <span className="pl-3 text-xs text-foreground-muted">
                  {t("input.generalFilters")}
                </span>
                <div
                  ref={filtersRef}
                  className="flex flex-col gap-y-2 max-mobile:pb-16"
                >
                  <FilterDropdown
                    label={t("input.country")}
                    items={countries}
                    selected={selectedCountries}
                    onSelect={handleSelectedCountry}
                    placeholderIcon={flagIcon}
                  />
                  <FilterDropdown
                    label={t("input.league")}
                    items={leagues}
                    selected={selectedLeagues}
                    onSelect={handleSelectedLeague}
                    placeholderIcon={leagueIcon}
                  />
                  <FilterDropdown
                    label={t("input.club")}
                    items={clubs}
                    selected={selectedClubs}
                    onSelect={handleSelectedClub}
                    placeholderIcon={clubsIcon}
                  />
                  {liveEvents.length > 0 && (
                    <>
                      <span className="mt-2 flex items-center gap-x-2 pl-3 text-xs text-foreground-muted">
                        <span>{t("input.liveEvent")}</span>
                        {selectedLiveEventId === undefined && <NewTag />}
                      </span>
                      <Dropdown
                        items={liveEventItems}
                        selected={
                          selectedLiveEventId === undefined
                            ? NO_LIVE_EVENT
                            : String(selectedLiveEventId)
                        }
                        placeholder={t("input.liveEvent")}
                        onChange={(item) =>
                          dispatch(
                            selectLiveEvent(
                              item.value === NO_LIVE_EVENT
                                ? undefined
                                : Number(item.value),
                            ),
                          )
                        }
                        variant="secondary"
                        hideSearch
                        width={filtersWidth}
                      />
                    </>
                  )}
                  <div className="flex flex-col items-center gap-y-3 mt-4 relative w-fit mx-auto">
                    <Button
                      variant="tertiary"
                      size="small"
                      icon={<ChevronRight />}
                      iconPosition="right"
                      onClick={(e) => {
                        handleClickMoreFilters();
                        e.stopPropagation();
                      }}
                    >
                      {t("panel.moreGeneralFilters")}
                    </Button>
                    <TextButton onClick={handleClearAllFilters}>
                      {t("input.clearAllFilters", {
                        amount: activeFilterCount,
                      })}
                    </TextButton>
                  </div>
                </div>
              </div>
            </div>
          </ComponentContainer>
          {highlightFilters && (
            <p className="mt-2 text-center text-xs text-neutral-100 motion-safe:animate-hint-fade">
              {t("customize.hint")}
            </p>
          )}
        </div>
        {desktopMode && (
          <div
            className={cn(
              "mt-4 relative flex flex-col items-center",
              onBoardingStep === 1 && "z-21 pointer-events-none",
            )}
          >
            {onBoardingStep === 1 && (
              <div className="absolute -left-14">
                <OnBoardingNumber number={2} />
              </div>
            )}
            <SuggestSquadsButton
              loading={statusBuildSquad === "pending"}
              onClick={handleClickedBuildSquads}
              chemOptimizing={chemOptimizing}
              usesClub={usesClubPool(scope)}
              // disabled
            />
          </div>
        )}
      </div>
    </>
  );
};
